import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Zap } from 'lucide-react';
export function IntroSection() {
  return <section className="py-20 bg-white" data-id="element-50">
      <div className="container mx-auto px-4 md:px-6" data-id="element-51">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-52">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-53">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-54">
              Simplify your workflow with{' '}
              <span className="text-teal-600" data-id="element-55">HomeLinkPro</span>
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-56">
              HomeLinkPro is a subscription-based SaaS tool that simplifies task
              management, communication and file sharing. No installation or
              complex setup required—just secure, cloud-based collaboration for
              your team.
            </p>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-57">
              Whether you're coordinating a remote team or managing on-site
              projects, our platform provides the clarity and control you need
              to succeed.
            </p>

            <div className="space-y-4" data-id="element-58">
              <div className="flex items-start gap-4" data-id="element-59">
                <div className="p-2 bg-orange-100 text-orange-600 rounded-lg mt-1" data-id="element-60">
                  <Layers size={20} data-id="element-61" />
                </div>
                <div data-id="element-62">
                  <h4 className="font-semibold text-slate-900" data-id="element-63">
                    All-in-one Dashboard
                  </h4>
                  <p className="text-slate-600 text-sm" data-id="element-64">
                    Everything you need in one place.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-65">
                <div className="p-2 bg-teal-100 text-teal-600 rounded-lg mt-1" data-id="element-66">
                  <Zap size={20} data-id="element-67" />
                </div>
                <div data-id="element-68">
                  <h4 className="font-semibold text-slate-900" data-id="element-69">
                    Instant Setup
                  </h4>
                  <p className="text-slate-600 text-sm" data-id="element-70">
                    Get started in minutes, not days.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-71">
            {/* Decorative background blob */}
            <div className="absolute inset-0 bg-gradient-to-tr from-teal-500/10 to-orange-500/10 rounded-3xl transform rotate-3" data-id="element-72"></div>

            {/* Browser mockup */}
            <div className="relative bg-white border-2 border-slate-200 rounded-2xl shadow-2xl overflow-hidden" data-id="element-73">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border-b border-slate-200" data-id="element-74">
                <div className="flex gap-2" data-id="element-75">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-76"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-77"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-78"></div>
                </div>
              </div>

              {/* Browser content with colourful sections */}
              <div className="p-6 space-y-4 bg-slate-50" data-id="element-79">
                {/* Header bar */}
                <div className="flex items-center gap-3" data-id="element-80">
                  <div className="h-3 bg-gradient-to-r from-teal-400 to-teal-600 rounded w-32" data-id="element-81"></div>
                  <div className="h-3 bg-slate-200 rounded w-20" data-id="element-82"></div>
                </div>

                {/* Colourful cards grid */}
                <div className="grid grid-cols-3 gap-3 mt-6" data-id="element-83">
                  <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: 0.4
                }} className="h-24 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg shadow-md" data-id="element-84"></motion.div>
                  <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: 0.5
                }} className="h-24 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg shadow-md" data-id="element-85"></motion.div>
                  <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: 0.6
                }} className="h-24 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg shadow-md" data-id="element-86"></motion.div>
                </div>

                {/* Content area with gradient */}
                <motion.div initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: 0.7
              }} className="h-32 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center" data-id="element-87">
                  <div className="text-center" data-id="element-88">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-teal-400 to-orange-500 rounded-full opacity-20" data-id="element-89"></div>
                    <div className="h-2 bg-slate-300 rounded w-24 mx-auto mb-2" data-id="element-90"></div>
                    <div className="h-2 bg-slate-300 rounded w-16 mx-auto" data-id="element-91"></div>
                  </div>
                </motion.div>

                {/* Bottom action bar */}
                <div className="flex gap-2 pt-2" data-id="element-92">
                  <div className="h-8 bg-gradient-to-r from-teal-500 to-teal-600 rounded flex-1 shadow-sm" data-id="element-93"></div>
                  <div className="h-8 bg-slate-200 rounded w-20" data-id="element-94"></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}